import React from "react";
import { Mic, Sparkles, GraduationCap, Repeat, TrendingUp, Lightbulb, RotateCcw, ArrowRight, CheckCircle2 } from "lucide-react";
import FeaturePage, { MockWindow, ScoreBar } from "./FeaturePage";
import { siblingLinks } from "./links";

const ACCENT = "#0025D8";
const COACH = "#7C3AED";   // matches the in-product "Coach me on this" accent
const GREEN = "#10B981";

// ── Hero visual: a live scoring session card ──
function HeroVisual() {
  return (
    <MockWindow label="LIVE SESSION · TECHNICAL ROUND" badge="● REC" accent="#E11D48">
      <div className="space-y-4">
        <div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] mb-1.5">Q3 OF 5</div>
          <p className="font-semibold text-sm leading-snug text-[#0F172A]">
            You're launching a new product on a limited budget. How would you structure a multi-channel campaign, and which channels would you prioritise?
          </p>
        </div>
        <div className="bg-secondary/40 border border-border rounded-xl p-3.5 text-xs text-muted-foreground leading-relaxed italic">
          "I'd start by defining the target segment and the one metric that matters, then weight budget toward the two channels with the best historical CAC…"
        </div>
        <ScoreBar label="AI SCORE · STRONG ANSWER" value={84} accent={ACCENT} suffix="/100" />
        <div className="grid grid-cols-3 gap-2">
          {[{ l: "WPM", v: "138" }, { l: "FILLERS", v: "2" }, { l: "FOCUS", v: "94%" }].map((s) => (
            <div key={s.l} className="bg-secondary/40 border border-border rounded-xl p-2.5 text-center">
              <div className="font-mono text-[9px] text-muted-foreground tracking-[0.1em] mb-0.5">{s.l}</div>
              <div className="font-display font-bold text-sm text-[#0F172A]">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row 1 visual: round selector ──
function RoundsVisual() {
  const rounds = [
    { t: "HR", d: "Motivation, culture-fit, behavioural basics", on: true },
    { t: "Technical", d: "Role-specific depth, problem solving", on: true },
    { t: "Behavioral", d: "STAR-method situational questions", on: true },
  ];
  return (
    <MockWindow label="INTERVIEW SETUP" accent={ACCENT}>
      <div className="space-y-2.5">
        {rounds.map((r) => (
          <div key={r.t} className="flex items-center gap-3 rounded-xl border border-border p-3.5"
            style={{ borderColor: r.on ? `${ACCENT}33` : undefined, background: r.on ? `${ACCENT}0D` : undefined }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${ACCENT}14`, color: ACCENT }}>
              <Mic className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm text-[#0F172A]">{r.t} round</div>
              <div className="text-[11px] text-muted-foreground truncate">{r.d}</div>
            </div>
            <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: ACCENT }}>
              <div className="w-1.5 h-1.5 bg-white rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row 2 visual: report card ──
function ReportVisual() {
  return (
    <MockWindow label="SESSION REPORT" badge="Overall 82" accent={ACCENT}>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {[{ l: "HR", v: 88 }, { l: "TECHNICAL", v: 79 }, { l: "BEHAVIORAL", v: 80 }].map((s) => (
            <div key={s.l} className="rounded-xl border border-border p-3 text-center">
              <div className="font-mono text-[9px] text-muted-foreground tracking-[0.1em] mb-1">{s.l}</div>
              <div className="font-display font-black text-xl text-[#0F172A]">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-border p-3.5">
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2 flex items-center gap-1.5" style={{ color: ACCENT }}>
            <Sparkles className="w-3 h-3" /> STRENGTHS
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">Clear structure, strong concrete examples, confident pacing at 138 wpm.</p>
        </div>
        <div className="rounded-xl border p-3.5" style={{ borderColor: "#F59E0B33", background: "#F59E0B0D" }}>
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2 text-[#B45309]">TO IMPROVE</div>
          <p className="text-xs text-muted-foreground leading-relaxed">Trim filler words when weighing budget trade-offs; quantify impact with metrics.</p>
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: "Coach me on this" panel ──
function CoachVisual() {
  return (
    <MockWindow label="COACH ME ON THIS" accent={COACH}>
      <div className="space-y-4">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
          style={{ background: `${COACH}14`, color: COACH }}>
          <Lightbulb className="w-3.5 h-3.5" /> Coach me on this
        </div>
        <div>
          <div className="font-mono text-[10px] font-bold tracking-[0.2em] mb-2 flex items-center gap-1.5" style={{ color: GREEN }}>
            <CheckCircle2 className="w-3 h-3" /> WHAT YOU DID WELL
          </div>
          <ul className="space-y-1.5">
            {["Led with a clear, structured answer", "Backed it with a concrete example"].map((s) => (
              <li key={s} className="flex items-start gap-2 text-xs text-[#0F172A] leading-snug">
                <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GREEN }} /> {s}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-mono text-[10px] font-bold tracking-[0.2em] mb-2 flex items-center gap-1.5" style={{ color: COACH }}>
            <ArrowRight className="w-3 h-3" /> HOW TO IMPROVE
          </div>
          <ul className="space-y-1.5">
            {["Quantify the outcome — put a number on the impact", "Close by tying it back to the role's needs"].map((t) => (
              <li key={t} className="flex items-start gap-2 text-xs text-[#0F172A] leading-snug">
                <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: COACH }} /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: retake history with improving scores ──
function RetakeVisual() {
  const attempts = [
    { label: "ORIGINAL", v: 64, tone: "#F59E0B" },
    { label: "RETAKE 1", v: 78, tone: "#0025D8" },
    { label: "RETAKE 2", v: 86, tone: GREEN },
  ];
  return (
    <MockWindow label="RETAKE HISTORY" badge="64 → 86" accent={ACCENT}>
      <div className="space-y-2.5">
        {attempts.map((a) => (
          <div key={a.label} className="flex items-center gap-3 rounded-xl border border-border p-3">
            <span className="font-mono text-[9px] tracking-[0.15em] text-muted-foreground w-16 flex-shrink-0">{a.label}</span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: `${a.tone}20` }}>
              <div className="h-1.5 rounded-full" style={{ width: `${a.v}%`, background: a.tone }} />
            </div>
            <span className="font-display font-black text-lg w-8 text-right" style={{ color: a.tone }}>{a.v}</span>
          </div>
        ))}
        <button className="w-full flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-xs font-bold mt-1"
          style={{ borderColor: `${ACCENT}40`, color: ACCENT, background: `${ACCENT}0D` }}>
          <RotateCcw className="w-3.5 h-3.5" /> Retake this question
        </button>
        <p className="text-[11px] text-muted-foreground text-center">Every attempt is kept — your baseline is never overwritten.</p>
      </div>
    </MockWindow>
  );
}

export default function MockInterviewFeature() {
  return (
    <FeaturePage
      accent={ACCENT}
      eyebrow="MOCK INTERVIEWS"
      title="Practice real interviews,"
      titleAccent="out loud."
      subtitle="A hands-free AI interviewer that speaks the questions, listens to your spoken answers, and scores every response against real reference material — HR, technical, and behavioural rounds built for actual roles."
      heroChips={["Instant AI scoring", "Per-answer coaching", "Retake & track progress"]}
      heroVisual={<HeroVisual />}
      rows={[
        {
          tag: "ROLE-BASED ROUNDS",
          title: "Curated questions for the job you're chasing.",
          body: "Pick your role and the rounds you want to drill. Each round pulls from a curated question bank built for real interviews — not generic filler — so you practise what you'll actually be asked.",
          bullets: [
            "HR, Technical, and Behavioural rounds",
            "Questions matched to your target role",
            "Set how many questions per round",
          ],
          visual: <RoundsVisual />,
        },
        {
          tag: "SPOKEN, HANDS-FREE",
          title: "Speak your answers — just like the real thing.",
          body: "The interviewer reads each question aloud and transcribes your spoken reply in real time. No typing, no reading off a script — you rehearse the actual muscle of answering under pressure.",
          bullets: [
            "Natural voice questions and follow-ups",
            "Live speech-to-text of your answers",
            "Speech delivery metrics: pace, fillers, focus",
          ],
          visual: <HeroVisual />,
          reverse: true,
        },
        {
          tag: "SCORED FEEDBACK",
          title: "Know exactly where you stand — and what to fix.",
          body: "Every answer is graded 0–100 against reference material, with a per-round breakdown, clear strengths, and specific things to improve.",
          bullets: [
            "Per-round and overall scores",
            "Concrete strengths and fixes",
            "Speech-delivery metrics on every answer",
          ],
          visual: <ReportVisual />,
        },
        {
          tag: "COACH ME ON THIS",
          title: "Turn any answer into a coaching session.",
          body: "Stuck on a question? Hit “Coach me on this” and the AI breaks down what you did well and exactly how to improve — specific, actionable pointers, not vague scores. Your coaching notes are saved so you can revisit them any time.",
          bullets: [
            "Per-question “what you did well” + “how to improve”",
            "Concrete, actionable fixes — not generic tips",
            "Saved to your report to review before the real thing",
          ],
          visual: <CoachVisual />,
          reverse: true,
        },
        {
          tag: "RETAKE & IMPROVE",
          title: "Retake it. Watch the score climb.",
          body: "Redo a single question or a whole round after you've seen the feedback. Every attempt is kept as history — your original baseline is never overwritten — so you get a clear before-and-after of your progress.",
          bullets: [
            "Retake one question or an entire round",
            "Full attempt history, baseline preserved",
            "See your score improve, attempt over attempt",
          ],
          visual: <RetakeVisual />,
        },
      ]}
      steps={[
        { title: "Pick your role & rounds", body: "Choose the position you're interviewing for and which rounds to practise." },
        { title: "Answer out loud", body: "The AI asks each question by voice; you reply naturally while it transcribes and follows up." },
        { title: "Get your scored report", body: "See per-round scores, strengths, and fixes — then retake to improve." },
      ]}
      useCases={[
        { icon: GraduationCap, persona: "New grads", body: "Build the reps and confidence to handle your first real interview without freezing on the tough questions." },
        { icon: Repeat, persona: "Career switchers", body: "Rehearse how to frame transferable experience so a panel sees the fit, not the gap." },
        { icon: TrendingUp, persona: "Senior ICs & managers", body: "Sharpen your structured thinking and behavioural storytelling before a high-stakes loop — and quantify your impact." },
      ]}
      faqs={[
        { q: "Do I have to speak, or can I type?", a: "It's built for speaking. The AI reads each question aloud and transcribes your spoken answer in real time, so you rehearse the actual pressure of answering out loud — the part that trips most people up." },
        { q: "How is my answer scored?", a: "Each answer is graded 0–100 against reference material for the role, with a per-round breakdown plus specific strengths and things to improve. You also get speech-delivery metrics like pace and filler-word count." },
        { q: "Can I practise a specific role?", a: "Yes. Pick your target role and the rounds you want — HR, Technical, Behavioural — and questions are drawn from a curated bank built for that role rather than generic filler." },
        { q: "What is “Coach me on this”?", a: "On any answer in your report you can tap “Coach me on this” to get an AI breakdown of what you did well and exactly how to improve — specific, actionable pointers. Your coaching notes are saved so you can review them before the real interview." },
        { q: "Can I retake a question or round?", a: "Yes — redo a single question or an entire round after seeing your feedback. Every attempt is kept as history and your original baseline is never overwritten, so you get a clear before-and-after of your progress." },
        { q: "Is it really free to start?", a: "Yes — you can start practising for free, no credit card required." },
      ]}
      ctaTitle="Walk in prepared."
      ctaSubtitle="Free to start. No credit card required."
      related={siblingLinks("mock-interview")}
      seoTitle="AI Mock Interviews — Voice Practice with Instant Scoring | VetraHub"
      seoDescription="Practise real interviews out loud with an AI interviewer. HR, technical, and behavioural rounds scored 0–100 with speech-delivery feedback. Free to start."
    />
  );
}
